import { createCheckoutService, createEmbeddedCheckoutMessenger, createStepTracker, StepTracker } from '@bigcommerce/checkout-sdk';
import { BrowserOptions } from '@sentry/browser';
import React, { Component } from 'react';
import ReactModal from 'react-modal';
import { Provider } from 'react-redux';

import '../../scss/App.scss';
import B3Checkout from '../b3checkout';
import { createErrorLogger, ErrorBoundary, ErrorLogger } from '../common/error';
import { createEmbeddedCheckoutStylesheet, createEmbeddedCheckoutSupport } from '../embeddedCheckout';
import { getLanguageService, LocaleProvider } from '../locale';

import Checkout from './Checkout';
import CheckoutProvider from './CheckoutProvider';

export interface CheckoutAppProps {
    checkoutId: string;
    containerId: string;
    publicPath?: string;
    sentryConfig?: BrowserOptions;
}

export default class CheckoutApp extends Component<CheckoutAppProps> {
    private checkoutService = createCheckoutService({
        locale: getLanguageService().getLocale(),
        shouldWarnMutation: process.env.NODE_ENV === 'development',
    });
    private embeddedStylesheet = createEmbeddedCheckoutStylesheet();
    private embeddedSupport = createEmbeddedCheckoutSupport(getLanguageService());
    private errorLogger: ErrorLogger;

    constructor(props: Readonly<CheckoutAppProps>) {
        super(props);

        this.errorLogger = createErrorLogger(
            { sentry: props.sentryConfig },
            {
                errorTypes: ['UnrecoverableError'],
                publicPath: props.publicPath,
            }
        );
    }

    componentDidMount(): void {
        const { containerId } = this.props;

        ReactModal.setAppElement(`#${containerId}`);
    }

    componentWillUnmount(): void {
        localStorage.removeItem('quoteCheckoutId')
        localStorage.removeItem('quoteDate')
    }

    render() {
        return (
            <Provider store={ B3Checkout.store }>
                <ErrorBoundary logger={ this.errorLogger }>
                    <LocaleProvider checkoutService={ this.checkoutService }>
                        <CheckoutProvider checkoutService={ this.checkoutService }>
                            <Checkout
                                { ...this.props }
                                createEmbeddedMessenger={ createEmbeddedCheckoutMessenger }
                                createStepTracker={ this.createStepTracker }
                                embeddedStylesheet={ this.embeddedStylesheet }
                                embeddedSupport={ this.embeddedSupport }
                                errorLogger={ this.errorLogger }
                            />
                        </CheckoutProvider>
                    </LocaleProvider>
                </ErrorBoundary>
            </Provider>
        );
    }

    private createStepTracker: () => StepTracker = () => {
        return createStepTracker(this.checkoutService);
    };
}
